import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { Box, Portal, Typography } from '@mui/material'
import { Icon } from 'src/components/atoms'
import styles from './styles.module.scss'

// フォーカストラップの対象。button 限定だと将来リンクや tabIndex を持つ要素が
// 追加されたときにトラップから漏れるため、標準的なフォーカス可能要素を列挙する。
const focusableSelector = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

export type MemoImageViewerProps = {
  images: string[]
  /** 表示中の画像の index。images の範囲内であること */
  index: number
  /** ヘッダー右に出す補助テキスト（報告日時など） */
  subText?: string
  onChangeIndex: (index: number) => void
  onClose: () => void
} & React.StyledProps

/**
 * 補足事項の添付画像を全画面で開くビューア。
 * 複数枚のときはフッターの矢印で前後に送れる。
 */
const MemoImageViewer: React.StyledFC<MemoImageViewerProps> = ({
  className,
  images,
  index,
  subText,
  onChangeIndex,
  onClose,
}: MemoImageViewerProps) => {
  const total = images.length
  const src = images[index]
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  // 署名付きURLの期限切れ等で読み込みに失敗した画像。index ではなく署名込みの
  // URL をキーにすることで、親の再取得で新しい署名付きURLが渡されれば別キーになり
  // 読み込みが再試行される（ビューアを開いたままでも期限切れから復旧できる）。
  // 壊れた画像アイコンのまま放置せず、プレースホルダーに差し替える。
  const [failedSrcs, setFailedSrcs] = useState<Set<string>>(new Set())

  // Escape で閉じられるようにする（マウス操作に依存させない）。
  // あわせて aria-modal="true" の通り、Tab のフォーカス移動を
  // ダイアログ内の要素だけに閉じ込める（背面のページ要素へ逃がさない）。
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const root = rootRef.current
      if (!root) return
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(focusableSelector)
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement
      const isInDialog = active instanceof HTMLElement && root.contains(active)
      if (e.shiftKey) {
        if (!isInDialog || active === first) {
          e.preventDefault()
          last.focus()
        }
      } else if (!isInDialog || active === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return (): void => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // 開いた直後は閉じるボタンへフォーカスを移し、キーボードでも操作を始められるようにする
  useEffect(() => {
    closeButtonRef.current?.focus()
  }, [])

  // 全画面で覆っている間は背面のページをスクロールさせない
  useEffect(() => {
    const { body } = document
    const previousOverflow = body.style.overflow
    body.style.overflow = 'hidden'
    return (): void => {
      body.style.overflow = previousOverflow
    }
  }, [])

  // 再取得で images が縮む等で index が範囲外になったとき、描画を止めるだけだと
  // 親の「開いている」state が残り続ける。閉じたことを明示的に伝えて畳んでもらう。
  useEffect(() => {
    if (!src) onClose()
  }, [src, onClose])

  if (!src) {
    return null
  }

  return (
    // 精査画面のサイドバー(.bodyRightSideBar)は flex item に z-index を持ち
    // 重なり文脈を作るため、その中に置くとページのヘッダー/フッターより
    // 前面に出せない。body 直下へ portal して全画面を覆う。
    <Portal>
      <Box
        ref={rootRef}
        className={clsx(className, styles.root)}
        data-testid="memo-image-viewer"
        role="dialog"
        aria-modal="true"
        aria-label="補足事項の添付画像"
        // MUI の Dialog(theme.zIndex.modal) と同値だと重なり順が DOM 順に依存するため、
        // 必ず前面に来るよう theme のスケールを基準に 1 つ上へ置く。
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <Box className={styles.header}>
          <button
            ref={closeButtonRef}
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="閉じる"
          >
            <Icon className={styles.closeIcon} icon="close" />
          </button>
          {subText && (
            <Typography className={styles.subText}>{subText}</Typography>
          )}
        </Box>

        <Box className={styles.body}>
          <Box className={styles.imageFrame}>
            {failedSrcs.has(src) ? (
              <Box className={styles.imageFallback}>
                <Typography className={styles.imageFallbackText}>
                  画像を表示できません
                </Typography>
              </Box>
            ) : (
              <img
                className={styles.image}
                src={src}
                alt={`添付画像${index + 1}`}
                onError={(): void =>
                  setFailedSrcs((prev) => new Set(prev).add(src))
                }
              />
            )}
          </Box>
        </Box>

        <Box className={styles.footer}>
          <button
            type="button"
            className={styles.pagerButton}
            onClick={(): void => onChangeIndex(index - 1)}
            disabled={index <= 0}
            aria-label="前の画像"
          >
            <Icon className={styles.prevIcon} icon="navigateNext" />
          </button>
          <Typography className={styles.pageNumber}>
            {`${index + 1}/${total}枚目`}
          </Typography>
          <button
            type="button"
            className={styles.pagerButton}
            onClick={(): void => onChangeIndex(index + 1)}
            disabled={index >= total - 1}
            aria-label="次の画像"
          >
            <Icon className={styles.nextIcon} icon="navigateNext" />
          </button>
        </Box>
      </Box>
    </Portal>
  )
}

export default MemoImageViewer
